import React, {useMemo} from 'react';
import type {BoxProps} from '@tempo/core';
import {Tr} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import type {Column} from '@components/Table/types';
import type {TableContextType} from '@components/Table';
import Table, {TableContext} from '@components/Table';
import TableHeader from '@components/Table/core/TableHeader';
import HeaderCell from '@components/Table/cells/HeaderCell';
import {ClaimTableColumn} from './types';
import ClaimTableBody from './ClaimTableBody';
import type {Claim} from '@deezer/next/services/api/Starmania/requests/claim/types';
import {useUser} from '@deezer/next/contexts/main/hooks';
import {StakeholderType} from '@deezer/next/contexts/main/types';

type ClaimTableProps = {
	claims: Claim[];
	onRemove: (claim: Claim) => void;
} & BoxProps;

const ClaimTableContext = TableContext as TableContextType<ClaimTableColumn>;

const ClaimTable = ({claims, onRemove, ...rest}: ClaimTableProps) => {
	const {t} = useTranslation('d4c');
	const user = useUser();
	const columns: Column<ClaimTableColumn>[] = useMemo(() => {
		let detailsLabel = t('generic_title_description_d4c-web');
		switch (user?.stakeholderType) {
			case StakeholderType.PROVIDER:
				detailsLabel = t('deliverymonitoring_title_provider_d4c-web');
				break;
			case StakeholderType.LABEL_GROUP:
				detailsLabel = t('pitchingtoolform_text_label_d4c-web');
				break;
			case StakeholderType.ARTIST_MANAGER:
				detailsLabel = t('analyticshome_title_artists_d4c-web');
				break;
			case StakeholderType.ARTIST:
				detailsLabel = t('analyticsmanager_text_artist_d4c-web');
				break;
			default:
				break;
		}

		return [
			{
				id: ClaimTableColumn.DETAILS,
				label: detailsLabel,
			},
			{
				id: ClaimTableColumn.DATE,
				label: t('pitchingtool_text_creationdate_d4c-web'),
				display: {base: false, sm: true},
				width: '160px',
			},
			{
				id: ClaimTableColumn.STATUS,
				label: t('usermanager_title_status_d4c-web'),
				width: {base: '90px', md: '120px'},
			},
			{
				id: ClaimTableColumn.DELETE,
				width: '48px',
			},
		];
	}, [t, user?.stakeholderType]);

	return (
		<Table
			columns={columns}
			flex={1}
			layout="fixed"
			stickyHeader={true}
			{...rest}
		>
			<ClaimTableContext.Consumer>
				{({columns}) => (
					<>
						<TableHeader>
							<Tr>
								{columns.map(column => {
									return (
										<HeaderCell
											key={column.id}
											label={column.label || ''}
											width={column.width}
											display={column.display}
										/>
									);
								})}
							</Tr>
						</TableHeader>
						<ClaimTableBody data={claims} onRemove={onRemove} />
					</>
				)}
			</ClaimTableContext.Consumer>
		</Table>
	);
};

export default ClaimTable;
